/**
 * API client for the tracker feature. Uses the shared `ApiError` +
 * auth-header plumbing from `@/lib/api` by re-implementing the thin
 * fetch wrapper locally (imports aren't exported for module-private
 * helpers). All functions return the unwrapped payload — envelope
 * handling is done here.
 */

import { ApiError, errorFromResponse } from '@/lib/api';
import { getStoredToken, clearAuth } from '@/features/auth/utils/authStorage';

import type {
  ApplyPart,
  BoardRefResponse,
  LinkTypeResponse,
  BoardScaffoldResponse,
  ClearCacheResponse,
  CreateDatasetRequest,
  CreateSourceRequest,
  DatasetPreviewRequest,
  DatasetPreviewResponse,
  DatasetResponse,
  IngestionResponse,
  LatestIngestionResponse,
  QueryPreviewResponse,
  SimulationTeamTrackerState,
  SourceResponse,
  TeamRefreshResponse,
  TeamThroughputResponse,
  ThroughputSettingsBody,
  TrackerCapabilities,
  TrackerField,
  UpdateDatasetRequest,
  UpdateSourceRequest,
} from './types';

const API_BASE_URL = '/api';

function authHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function handleAuthError(): void {
  clearAuth();
  const path = window.location.pathname;
  if (path !== '/login' && path !== '/setup') {
    window.location.href = '/login';
  }
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...(init.headers ?? {}),
    },
  });
  if (response.status === 401) {
    handleAuthError();
    throw new ApiError('Unauthorized', 401, 'UNAUTHORIZED');
  }
  if (response.status === 204) {
    return undefined as T;
  }
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  const text = await response.text();
  const json = JSON.parse(text) as { data: T };
  return json.data;
}

// --- Capabilities ------------------------------------------------------------

/**
 * Entitlement + configuration state for the tracker feature. Reachable in
 * every state (not behind the license/operability gates), so the UI can read
 * it to decide what to render instead of provoking a 403/503.
 */
export async function getCapabilities(): Promise<TrackerCapabilities> {
  return request<TrackerCapabilities>('/tracker/capabilities');
}

// --- Sources -----------------------------------------------------------------

export async function listSources(): Promise<SourceResponse[]> {
  return request<SourceResponse[]>('/tracker/sources');
}

export async function createSource(req: CreateSourceRequest): Promise<SourceResponse> {
  return request<SourceResponse>('/tracker/sources', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function updateSource(id: string, req: UpdateSourceRequest): Promise<SourceResponse> {
  return request<SourceResponse>(`/tracker/sources/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(req),
  });
}

export async function deleteSource(id: string): Promise<void> {
  await request<void>(`/tracker/sources/${id}`, { method: 'DELETE' });
}

/**
 * Clear the cached work items for one tracker source, forcing the next ingest
 * on that tracker instance to refetch everything.
 *
 * Instance-scoped, not dataset-scoped: the returned `source_id` is the cache
 * partition (`jira:<host>`), shared by every dataset pointed at that host.
 */
export async function clearSourceCache(id: string): Promise<ClearCacheResponse> {
  return request<ClearCacheResponse>(`/tracker/sources/${id}/cache`, { method: 'DELETE' });
}

// --- Datasets ----------------------------------------------------------------

export async function listDatasets(): Promise<DatasetResponse[]> {
  return request<DatasetResponse[]>('/tracker/datasets');
}

export async function getDataset(id: string): Promise<DatasetResponse> {
  return request<DatasetResponse>(`/tracker/datasets/${id}`);
}

export async function createDataset(req: CreateDatasetRequest): Promise<DatasetResponse> {
  return request<DatasetResponse>('/tracker/datasets', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function updateDataset(id: string, req: UpdateDatasetRequest): Promise<DatasetResponse> {
  return request<DatasetResponse>(`/tracker/datasets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(req),
  });
}

export async function deleteDataset(id: string): Promise<void> {
  await request<void>(`/tracker/datasets/${id}`, { method: 'DELETE' });
}

// --- Ingestions --------------------------------------------------------------

export async function triggerIngest(datasetId: string): Promise<IngestionResponse> {
  return request<IngestionResponse>(`/tracker/datasets/${datasetId}/ingest`, {
    method: 'POST',
  });
}

export async function getIngestion(id: string): Promise<IngestionResponse> {
  return request<IngestionResponse>(`/tracker/ingestions/${id}`);
}

export async function getLatestIngestion(datasetId: string): Promise<LatestIngestionResponse> {
  return request<LatestIngestionResponse>(`/tracker/datasets/${datasetId}/ingestions/latest`);
}

// --- Simulation-team tracker state ------------------------------------------

export async function getTeamTrackerState(simulationTeamId: string): Promise<SimulationTeamTrackerState> {
  return request<SimulationTeamTrackerState>(`/tracker/simulation-teams/${simulationTeamId}`);
}

export async function setTeamDataset(
  simulationTeamId: string,
  datasetId: string | null,
): Promise<SimulationTeamTrackerState> {
  return request<SimulationTeamTrackerState>(`/tracker/simulation-teams/${simulationTeamId}/dataset`, {
    method: 'PATCH',
    body: JSON.stringify({ dataset_id: datasetId }),
  });
}

// --- A team's own refreshes and throughput (shipcast-o7j) -------------------

/**
 * Fetch the team's dataset fresh and apply one part of it to this team. The
 * fetch runs in the background: the response is the `running` refresh — or
 * the one already running for this part, which a second click joins.
 */
export async function startTeamRefresh(simulationTeamId: string, part: ApplyPart): Promise<TeamRefreshResponse> {
  return request<TeamRefreshResponse>(`/tracker/simulation-teams/${simulationTeamId}/refreshes`, {
    method: 'POST',
    body: JSON.stringify({ part }),
  });
}

/** The team's latest refresh of `part`; `null` if it never refreshed it. */
export async function getLatestTeamRefresh(
  simulationTeamId: string,
  part: ApplyPart,
): Promise<TeamRefreshResponse | null> {
  return request<TeamRefreshResponse | null>(
    `/tracker/simulation-teams/${simulationTeamId}/refreshes/latest?part=${part}`,
  );
}

/** The team's throughput settings, and the history they cut from its dataset. */
export async function getTeamThroughput(simulationTeamId: string): Promise<TeamThroughputResponse> {
  return request<TeamThroughputResponse>(`/tracker/simulation-teams/${simulationTeamId}/throughput`);
}

/** Save the team's throughput settings; the server re-cuts its throughput at once. */
export async function setTeamThroughputSettings(
  simulationTeamId: string,
  settings: ThroughputSettingsBody,
): Promise<TeamThroughputResponse> {
  return request<TeamThroughputResponse>(`/tracker/simulation-teams/${simulationTeamId}/throughput-settings`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

/**
 * The fields a tracker source reports as available (drives the adapter-config
 * and feature-import field pickers). A live probe — errors (tracker
 * unreachable, bad credentials) surface as `ApiError`; callers fall back to
 * free text.
 */
export async function getSourceFields(sourceId: string): Promise<TrackerField[]> {
  return request<TrackerField[]>(`/tracker/sources/${sourceId}/fields`);
}

/**
 * The boards a source can scaffold a workflow from. A live probe, like
 * `getSourceFields` — an unreachable tracker or rejected credential surfaces
 * as `ApiError` and the caller falls back to filling the form by hand.
 *
 * An empty array is a legitimate answer (a source with no boards, or one
 * whose kind has no board support), not an error.
 */
export async function listSourceBoards(sourceId: string): Promise<BoardRefResponse[]> {
  return request<BoardRefResponse[]>(`/tracker/sources/${sourceId}/boards`);
}

/**
 * The relationships this tracker can express between items, for the
 * dependency-link-type picker. A live probe like `getSourceFields`.
 *
 * An empty array is a legitimate answer, not an error: the upstream port
 * defaults this to empty and Azure DevOps takes that default. Callers must
 * treat empty and failed identically — offer free-text entry — because in
 * both cases the honest statement is "we do not know the vocabulary", and a
 * picker with no options would simply block the field.
 */
export async function listSourceLinkTypes(sourceId: string): Promise<LinkTypeResponse[]> {
  return request<LinkTypeResponse[]>(`/tracker/sources/${sourceId}/link-types`);
}

/**
 * A first-cut dataset config derived from one board: name, workflow columns
 * with their real status names, and the board's query when the tracker has
 * one.
 *
 * `boardId` is **opaque** and encoded, not interpolated raw: upstream sources
 * with a nested board hierarchy (Azure DevOps' team -> board) put a slash
 * inside it, which would otherwise split into two path segments and miss the
 * route entirely.
 */
export async function getBoardScaffold(sourceId: string, boardId: string): Promise<BoardScaffoldResponse> {
  return request<BoardScaffoldResponse>(`/tracker/sources/${sourceId}/boards/${encodeURIComponent(boardId)}/scaffold`);
}

/**
 * What a tracker query actually selects: an exact match count plus the first
 * few items. A live probe — a query the tracker rejects surfaces as an
 * `ApiError` carrying the tracker's own diagnostic, which names the field to
 * fix.
 *
 * POST, not GET: the query is user-authored text of unbounded length that
 * would otherwise sit in the URL, in access logs and in browser history.
 */
export async function previewSourceQuery(sourceId: string, query: string): Promise<QueryPreviewResponse> {
  return request<QueryPreviewResponse>(`/tracker/sources/${sourceId}/query-preview`, {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
}

/**
 * The warnings an ingest of this — unsaved — dataset config would record,
 * plus what it built: deliverable, work-item and dependency counts and the
 * throughput total. Runs the real ingest path against the tracker and saves
 * nothing, so every mistake the form can encode is seen before Save.
 *
 * A live run like `previewSourceQuery`, and the same error contract: the
 * tracker's own diagnostic for a rejected query, `tracker unreachable: …`
 * for a dead host — as an `ApiError` naming what to fix. A domain-invalid
 * workflow is refused exactly as Save refuses it (422 `INVALID_WORKFLOW`).
 */
export async function previewDataset(body: DatasetPreviewRequest): Promise<DatasetPreviewResponse> {
  return request<DatasetPreviewResponse>('/tracker/datasets/preview', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
